/**
 * Entscheidet, ob jemand angemeldet sein muss, bevor das Portal etwas zeigt.
 *
 * **Drei Zustände, und der mittlere ist der wichtige.**
 *
 * | Lage | Was geschieht |
 * |---|---|
 * | Kein Anbieter konfiguriert | Das Portal läuft ohne Anmeldung — der Demostand |
 * | Anbieter da, niemand angemeldet | Anmeldemaske, dann weiter zu Keycloak |
 * | Anbieter da, angemeldet | Die Anwendung, mit Namen und Gruppen aus dem Token |
 *
 * Der erste Fall ist kein Notbehelf: `pnpm demo` läuft ohne Kundenstack, und
 * ein Portal, das dort nach Anmeldung verlangt, wäre nicht mehr vorführbar.
 * Was ihn vom zweiten unterscheidet, ist **die Konfiguration des Kunden** und
 * nicht ein Schalter im Code — wer `domains.auth` und `realm` setzt, bekommt
 * die Anmeldung, wer sie wegläßt, bekommt sie nicht.
 *
 * **Angemeldet ist nicht berechtigt.** `01` §1.11: Die ACL prüft das
 * Quellsystem. Dieses Tor entscheidet, wer das Portal bedient — nicht, was er
 * darin sehen darf.
 */

import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { PortalKonfiguration } from "@hwc/config";
import type { IdentitaetsAnbieter } from "@hwc/auth";
import {
  anbieterAus,
  liesToken,
  meldeAb,
  RUECKWEG_PFAD,
  starteAnmeldung,
  verarbeiteRueckkehr,
  vergissToken,
  type Angemeldet,
} from "./anmeldung.js";

export interface Anmeldelage {
  /** Undefiniert, solange oder weil keine Anmeldung verlangt wird. */
  readonly wer?: Angemeldet;
  readonly anbieter?: IdentitaetsAnbieter;
  readonly abmelden?: () => void;
}

function herkunft(): string {
  return window.location.origin;
}

/**
 * Die Anmeldemaske.
 *
 * **Bewusst karg.** Sie zeigt keinen Anmeldeknopf mit Feldern für Benutzer und
 * Kennwort: Die Eingabe gehört zu Keycloak, und eine Maske, die hier danach
 * fragt, wäre entweder wirkungslos oder gefährlich. Zu üben, sein Kennwort
 * überall einzugeben, ist genau das, was Phishing braucht.
 */
function Anmeldemaske({
  marke,
  fehler,
  beiAnmelden,
}: {
  readonly marke: string;
  readonly fehler: string | null;
  readonly beiAnmelden: () => void;
}) {
  return (
    <main className="anmeldung" style={{ padding: "2rem", maxWidth: "34rem" }}>
      <h1>{marke}</h1>
      <p>
        Diese Arbeitsplattform verlangt eine Anmeldung. Sie erfolgt beim
        Identitätsanbieter des Betriebs, nicht auf dieser Seite.
      </p>
      {fehler ? (
        <p role="alert" data-hinweis="anmeldung">
          Die Anmeldung ist nicht zustande gekommen: {fehler}
        </p>
      ) : null}
      <button type="button" onClick={beiAnmelden}>
        Anmelden
      </button>
    </main>
  );
}

export function Anmeldetor({
  konfiguration,
  kinder,
}: {
  readonly konfiguration: PortalKonfiguration;
  readonly kinder: (lage: Anmeldelage) => ReactNode;
}) {
  const anbieter = anbieterAus(konfiguration);
  const [wer, setzeWer] = useState<Angemeldet | undefined>(undefined);
  const [fehler, setzeFehler] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  /*
   * **Die Rückkehr wird vor allem anderen verarbeitet.**
   *
   * Sie steht in der Adresszeile, und dort darf sie nicht bleiben: Ein Code
   * im Verlauf des Browsers ist ein Code in fremden Händen, sobald jemand
   * anders an den Rechner kommt. Nach dem Tausch wird die Adresse ersetzt,
   * nicht ergänzt — `replaceState`, damit der Zurück-Knopf nicht wieder
   * darauf zeigt.
   */
  useEffect(() => {
    if (!anbieter) return;
    if (window.location.pathname !== RUECKWEG_PFAD) {
      // Kein Rückweg: Vielleicht liegt eine gültige Anmeldung im Fenster.
      const token = liesToken();
      if (token) {
        try {
          setzeWer(ausToken(token.idToken, token));
        } catch {
          vergissToken();
        }
      }
      return;
    }

    setzeLaeuft(true);
    void verarbeiteRueckkehr(anbieter, herkunft(), window.location.search).then(
      (e) => {
        setzeLaeuft(false);
        if (e.art === "angemeldet") {
          setzeWer(e.wer);
          window.history.replaceState(null, "", e.weiter);
        } else if (e.art === "fehler") {
          setzeFehler(e.grund);
          window.history.replaceState(null, "", "/");
        }
      },
    );
  }, [anbieter]);

  const anmelden = useCallback(() => {
    if (!anbieter) return;
    setzeFehler(null);
    /*
     * **`void` ohne `catch` verschluckt den Grund.**
     *
     * Am 07.09.2026 am Piloten gemessen: Der Knopf tat nichts, die Adresse
     * blieb stehen, `sessionStorage` blieb leer — und in der Konsole stand
     * kein Wort. Eine abgelehnte Zusage, die niemand abfängt, ist genau der
     * Fehler, den `melden.py` an anderer Stelle vermeidet: Wer nicht laut
     * scheitert, laesst den Benutzer raten.
     */
    starteAnmeldung(anbieter, herkunft(), window.location.pathname).catch(
      (ursache: unknown) => {
        setzeFehler(
          ursache instanceof Error ? ursache.message : String(ursache),
        );
      },
    );
  }, [anbieter]);

  const abmelden = useCallback(() => {
    if (!anbieter) return;
    meldeAb(anbieter, herkunft());
  }, [anbieter]);

  // Ohne Anbieter läuft das Portal wie bisher. Das ist der Demostand.
  if (!anbieter) return <>{kinder({})}</>;

  if (laeuft) {
    return (
      <main style={{ padding: "2rem" }}>
        <p>Anmeldung wird abgeschlossen …</p>
      </main>
    );
  }

  if (!wer) {
    return (
      <Anmeldemaske
        marke={konfiguration.stackName || "Handwerker-Cloud"}
        fehler={fehler}
        beiAnmelden={anmelden}
      />
    );
  }

  return <>{kinder({ wer, anbieter, abmelden })}</>;
}

/**
 * Baut aus einem gemerkten Token wieder eine Anmeldung.
 *
 * **Ohne Ablaufprüfung wäre das ein stiller Fehler.** Ein abgelaufenes Token
 * sieht genauso aus wie ein gültiges; wer es unbesehen übernimmt, zeigt einen
 * Namen in der Kopfzeile und bekommt vom ersten Dienst eine Absage, die nach
 * einem Rechteproblem aussieht.
 */
function ausToken(
  idToken: string,
  token: { readonly idToken: string; readonly zugriffstoken: string; readonly gueltigFuer: number },
): Angemeldet {
  const teile = idToken.split(".");
  if (teile.length !== 3) throw new Error("Kein JWT.");
  const roh = teile[1] as string;
  const b64 = roh.replace(/-/g, "+").replace(/_/g, "/");
  const a = JSON.parse(
    atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)),
  ) as Record<string, unknown>;

  const ablauf = typeof a["exp"] === "number" ? a["exp"] : 0;
  if (ablauf * 1000 <= Date.now()) throw new Error("Token abgelaufen.");

  return {
    name: typeof a["name"] === "string" ? a["name"] : "",
    benutzername:
      typeof a["preferred_username"] === "string" ? a["preferred_username"] : "",
    gruppen: Array.isArray(a["groups"])
      ? (a["groups"] as unknown[]).filter((g): g is string => typeof g === "string")
      : [],
    ansprueche: a,
    token,
  };
}
